import { Injectable } from '@angular/core';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { Department, DepartmentNode } from '../models/department.model';
import { User } from '../models/user.model';

@Injectable({
  providedIn: 'root'
})
export class DepartmentService {
  // 部門模擬資料
  private mockDepartments: Department[] = [
    { id: 'd1', name: '總公司', parentId: null, level: 0 },
    { id: 'd2', name: '研發部門', parentId: 'd1', level: 1 },
    { id: 'd3', name: '行銷部門', parentId: 'd1', level: 1 },
    { id: 'd4', name: '人資部門', parentId: 'd1', level: 1 },
    { id: 'd5', name: '前端開發組', parentId: 'd2', level: 2 },
    { id: 'd6', name: '後端開發組', parentId: 'd2', level: 2 },
    { id: 'd7', name: '數位行銷組', parentId: 'd3', level: 2 },
    { id: 'd8', name: '品牌行銷組', parentId: 'd3', level: 2 },
    { id: 'd9', name: 'AI開發小組', parentId: 'd6', level: 3 },
  ];

  // 使用者模擬資料
  private mockUsers: User[] = [
    { id: 'u1', name: '王大明', email: 'wang@example.com', departmentIds: ['d1', 'd2'] },
    { id: 'u2', name: '李小英', email: 'lee@example.com', departmentIds: ['d2', 'd5'] },
    { id: 'u3', name: '張三豐', email: 'chang@example.com', departmentIds: ['d2', 'd6', 'd9'] },
    { id: 'u4', name: '陳小華', email: 'chen@example.com', departmentIds: ['d3', 'd7'] },
    { id: 'u5', name: '劉大龍', email: 'liu@example.com', departmentIds: ['d3', 'd8'] },
    { id: 'u6', name: '黃小明', email: 'huang@example.com', departmentIds: ['d4'] },
    { id: 'u7', name: '林美玲', email: 'lin@example.com', departmentIds: ['d5'] },
    { id: 'u8', name: '吳大鵬', email: 'wu@example.com', departmentIds: ['d6', 'd9'] },
    { id: 'u9', name: '鄭小婷', email: 'cheng@example.com', departmentIds: ['d7'] },
    { id: 'u10', name: '蕭大宇', email: 'hsiao@example.com', departmentIds: ['d8'] },
  ];

  // 用於追蹤選定的部門
  private selectedDepartmentSubject = new BehaviorSubject<Department | null>(null);
  selectedDepartment$ = this.selectedDepartmentSubject.asObservable();

  constructor() { }

  // 獲取部門樹狀結構
  getDepartmentTree(): Observable<DepartmentNode[]> {
    // 解決方案：完全重新實現樹狀結構構建邏輯

    // 1. 建立用於映射的 Map
    const deptMap = new Map<string, DepartmentNode>();

    // 2. 生成所有節點
    this.mockDepartments.forEach(dept => {
      const userCount = this.mockUsers.filter(u => u.departmentIds.includes(dept.id)).length;

      deptMap.set(dept.id, {
        id: dept.id,
        name: dept.name,
        level: dept.level,
        parentId: dept.parentId, // 添加 parentId 屬性
        userCount: userCount,
        order: dept.order || 0, // 添加順序屬性，如果沒有則預設為0
        children: []
      });
    });

    // 3. 構建樹狀結構，只有頂層節點才會添加到返回的陣列中
    const rootNodes: DepartmentNode[] = [];

    this.mockDepartments.forEach(dept => {
      // 獲取當前部門節點
      const currentNode = deptMap.get(dept.id);
      if (!currentNode) return;

      if (dept.parentId === null) {
        // 這是根節點，直接加入 rootNodes
        rootNodes.push(currentNode);
      } else {
        // 這是子節點，找到其父節點並添加為子項
        const parentNode = deptMap.get(dept.parentId);
        if (parentNode) {
          // 重要：確保不重複添加
          const alreadyAdded = parentNode.children.some(child => child.id === currentNode.id);
          if (!alreadyAdded) {
            parentNode.children.push(currentNode);
          }
        }
      }
    });

    console.log('Root nodes:', JSON.stringify(rootNodes));
    return of(rootNodes);
  }

  // 通過 ID 獲取部門
  getDepartmentById(id: string): Department | null {
    return this.mockDepartments.find(d => d.id === id) || null;
  }

  // 通過 ID 獲取用戶
  getUserById(id: string): User | null {
    return this.mockUsers.find(u => u.id === id) || null;
  }

  // 獲取部門中的用戶
  getUsersForDepartment(departmentId: string): Observable<User[]> {
    const users = this.mockUsers.filter(user =>
      user.departmentIds.includes(departmentId)
    );
    return of(users);
  }

  // 獲取不在部門中的用戶
  getUsersNotInDepartment(departmentId: string): Observable<User[]> {
    const users = this.mockUsers.filter(user =>
      !user.departmentIds.includes(departmentId)
    );
    return of(users);
  }

  // 獲取部門的用戶數量
  getUserCountForDepartment(departmentId: string): number {
    return this.mockUsers.filter(user =>
      user.departmentIds.includes(departmentId)
    ).length;
  }

  // 創建新部門
  createDepartment(department: Omit<Department, 'id'>): Observable<Department> {
    const newId = 'd' + (this.mockDepartments.length + 1);
    const newDepartment: Department = {
      id: newId,
      name: department.name,
      parentId: department.parentId,
      level: department.level
    };

    this.mockDepartments.push(newDepartment);
    return of(newDepartment);
  }

  // 更新部門
  updateDepartment(department: Department): Observable<Department> {
    const index = this.mockDepartments.findIndex(d => d.id === department.id);
    if (index !== -1) {
      this.mockDepartments[index] = department;
    }
    return of(department);
  }

  // 刪除部門
  deleteDepartment(departmentId: string): Observable<boolean> {
    // 檢查是否有子部門
    const hasChildren = this.mockDepartments.some(d => d.parentId === departmentId);

    if (hasChildren) {
      return of(false);  // 有子部門不能刪除
    }

    const index = this.mockDepartments.findIndex(d => d.id === departmentId);
    if (index !== -1) {
      this.mockDepartments.splice(index, 1);

      // 從所有用戶中移除這個部門ID
      this.mockUsers.forEach(user => {
        user.departmentIds = user.departmentIds.filter(id => id !== departmentId);
      });
    }

    return of(true);
  }

  // 添加用戶到部門
  addUserToDepartment(userId: string, departmentId: string): Observable<boolean> {
    const user = this.mockUsers.find(u => u.id === userId);
    if (user && !user.departmentIds.includes(departmentId)) {
      user.departmentIds.push(departmentId);
    }
    return of(true);
  }

  // 從部門移除用戶
  removeUserFromDepartment(userId: string, departmentId: string): Observable<boolean> {
    const user = this.mockUsers.find(u => u.id === userId);
    if (user) {
      user.departmentIds = user.departmentIds.filter(id => id !== departmentId);
    }
    return of(true);
  }

  // 選擇部門
  selectDepartment(department: Department): void {
    this.selectedDepartmentSubject.next(department);
  }
}
